<template>
  <div class="page-container">
    <div class="page-content">
      <div class="page-header">
        <div class="d-flex align-items-center mb-3">
          <h1 class="text-white">
            <i class="mdi mdi-checkbox-multiple-marked"></i>
            Apólices Emitidas
          </h1>
          <div class="d-flex gap-2 ml-auto align-items-center">
            <button
              v-if="$can.any(['policy-store', 'policy-update', 'policy-edit'])"
              @click="redirect('create-edit-policy')"
              class="btn btn-outline-white"
            >
              <i class="fa fa-plus-circle"></i>
              <span class="d-none d-md-inline-block ml-2">
                Inserir apólice emitida
              </span>
            </button>
            <button
              v-if="$can('policy-import')"
              @click="openImportPoliciesModal()"
              class="btn btn-outline-white"
            >
              <i class="fa fa-file-excel"></i>
              <span class="d-none d-md-inline-block ml-2">
                Importar apólices
              </span>
            </button>
            <button
                v-if="isMaster()"
                @click="openUploadPoliciesModal"
                class="btn btn-outline-white"
              >
                <i class="fas fa-file-upload"></i>
                <span class="d-none d-md-inline"> Importar PDF's</span>
              </button>

            <button
              v-if="$can('policy-export')"
              :disabled="loadingExport"
              @click="downloadReport()"
              class="btn btn-outline-white"
            >
              <i v-if="!loadingExport" class="fa fa-file-export"></i>
              <b-spinner v-else small></b-spinner>
              <span class="d-none d-md-inline-block ml-2">
                Exportar apólices
              </span>
            </button>
            <div class="btn-group">
              <button class="btn btn-outline-white" v-b-modal.modal-filter>
                <i class="fa fa-filter"></i>
                <span class="d-none d-md-inline-block ml-2">Filtros</span>
              </button>
              <button
                class="btn btn-outline-white"
                title="Limpar Filtros"
                @click="cleanFilter"
              >
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="loading"
        class="overlay-modal d-flex align-items-center justify-content-center"
      >
        <div>
          <div class="d-flex justify-content-center mb-2">
            <b-spinner label="Loading..."></b-spinner>
          </div>
          <div class="d-flex align-items-center">
            <strong>Carregando</strong>
          </div>
        </div>
      </div>

      <div class="page-body">
        <div class="card bg-light-gray">
          <div class="card-body pt-1">
            <div class="table-responsive">
              <table
                role="table"
                aria-busy="false"
                aria-colcount="11"
                class="table-v3"
              >
                <thead role="rowgroup">
                  <tr>
                    <th class="nowrap">ID</th>
                    <th class="nowrap">TIPO DE SEGURO</th>
                    <th class="nowrap text-center">SEGURADORA</th>
                    <th class="nowrap">IS</th>
                    <th class="nowrap">TOMADOR</th>
                    <th class="nowrap">SEGURADO</th>
                    <th class="nowrap">PRÊMIO</th>
                    <th class="nowrap">VENCIMENTO</th>
                    <th class="nowrap text-center">STATUS</th>
                    <th class="nowrap text-center">VENCE EM</th>
                    <th class="nowrap">AÇÃO</th>
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  <tr v-for="(item, index) in items" :key="index" role="row">
                    <td data-column-title="N.">
                      {{ item.number }}
                    </td>

                    <td
                      data-column-title="Tipo de Seguro"
                      :title="item.insurance_type_id"
                    >
                      <span class="truncate-125 truncate-xl-175">
                        {{ item.insurance_type_id }}
                      </span>
                    </td>

                    <td data-column-title="Seguradora" class="text-center">
                      <span v-if="!item.insurer && item.other_insurer">{{ item.other_insurer }}</span>
                      <span v-else-if="item.no_logo || !item.insurer">
                        {{ translateInsurerIdentifierTypes[item.insurer] }}
                      </span>
                      <img
                        v-else
                        :src="item.icon"
                        class="insurance-logo"
                        @error="imageLoadError(index)"
                      />
                    </td>

                    <td data-column-title="IS">
                      {{ formatCurrency(item.contract_value) }}
                    </td>

                    <td
                      data-column-title="Tomador"
                      :title="item.policy_holder_name"
                    >
                      <span class="truncate-100 truncate-xxl-150">
                        {{ truncateText(item.policy_holder_name) }}
                      </span>
                    </td>

                    <td data-column-title="Segurado" :title="item.insured_name">
                      <span class="truncate-100 truncate-xxl-150">
                        {{ truncateText(item.insured_name) }}
                      </span>
                    </td>

                    <td data-column-title="Prêmio Pago">
                      {{ formatCurrency(item.insurance_premium) }}
                    </td>

                    <td data-column-title="Vencimento">
                      {{ formatDateLocal(item.validity_expiration) }}
                    </td>

                    <td data-column-title="Status" class="text-center">
                      <span
                        v-b-tooltip.hover
                        :title="isRenewedAndFinished(item) ? 'Apólice renovada n. ' + item.renovations[0]?.policy?.number : ''"
                        :class="classStatusQuotation(getStatusId(item)) + (isRenewedAndFinished(item) ? ' cursor-pointer' : ' cursor-default')"
                        style="min-width: 120px"
                        @click="isRenewedAndFinished(item) && redirect('details-policy', { policyId: item.renovations[0].policy_id })"
                      >
                        {{ isInRenewal(item) ? "Em renovação" : getEnumKeyByEnumValue(translateStatusQuotationTypes, item.status) }}
                      </span>
                    </td>

                    <td data-column-title="Prazo p/ Vencer" class="text-center">
                      <span v-html="addHtmlClass(item)"></span>
                    </td>

                    <td data-column-title="Ação" class="actions">
                      <div class="btn-group">
                        <a
                          class="btn btn-action"
                          @click="
                            redirect('create-edit-policy', {
                              policyId: item.id,
                            })
                          "
                          v-b-tooltip.hover
                          title="Editar apólice"
                        >
                          <i class="fas fa-pen"></i>
                        </a>
                        <a
                          class="btn btn-action"
                          @click="
                            redirect('details-policy', { policyId: item.id })
                          "
                          v-b-tooltip.hover
                          title="Visualizar apólice"
                        >
                          <i class="fas fa-eye"></i>
                        </a>
                        <b-dropdown
                          no-caret
                          right
                          variant="link"
                          toggle-class="btn btn-action"
                          menu-class="dropdown-menu-right border"
                        >
                          <template #button-content>
                            <i class="fa fa-ellipsis-v"></i>
                          </template>

                          <b-dropdown-item
                            v-if="
                              $can.any([
                                'claim-store',
                                'claim-update',
                                'claim-edit',
                              ]) &&
                              item.status !==
                                translateStatusQuotationTypes['Cancelada']
                            "
                            @click="
                              redirect('create-claim', { policyId: item.id })
                            "
                            title="Reportar sinistro"
                          >
                            <i class="fa fa-fw mdi mdi-shield-alert"></i>
                            Reportar sinistro
                          </b-dropdown-item>

                          <b-dropdown-item
                            v-if="
                              $can.any([
                                'endorsement-store',
                                'endorsement-update',
                                'endorsement-edit',
                              ])
                            "
                            @click="
                              redirect('create-endorsement', {
                                policyId: item.id,
                              })
                            "
                            title="Criar endosso"
                          >
                            <i class="fa fa-fw mdi mdi-thumb-up"></i>
                            Criar endosso
                          </b-dropdown-item>

                          <b-dropdown-item
                            v-if="
                              $can('policy-store') &&
                              item.renovations.length === 0 &&
                              item.status !== translateStatusQuotationTypes['Cancelada'] &&
                              hasInsuranceCategoryIntegration(item.insurance_category_id)
                            "
                            @click="showModalCreate(item)"
                            title="Renovar apólice"
                          >
                            <i class="fa fa-sync"></i>
                            Renovar apólice
                          </b-dropdown-item>
                          <b-dropdown-item
                            v-if="
                              $can('policy-store') &&
                              item.renovations.length === 0 &&
                              item.status !== translateStatusQuotationTypes['Cancelada'] &&
                              isMaster()
                            "
                            @click="showModalAutomaticRenewal(item.id)"
                            title="Renovar apólice"
                          >
                            <i class="fa fa-clock"></i>
                            Renovação automática
                          </b-dropdown-item>
                          <b-dropdown-item
                            v-if="$can('policy-delete')"
                            @click="showModalDestroy(item)"
                            title="Apagar"
                          >
                            <i class="fas fa-trash"></i>
                            Apagar
                          </b-dropdown-item>
                        </b-dropdown>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

                  <div>
          <b-pagination
            @page-click="pageChanged"
            v-model="currentPage"
            :total-rows="rows"
            :per-page="perPage"
            class="my-3 mb-4"
          ></b-pagination>
        </div>

            <ImportPoliciesModal
              v-model="showImportPolicies"
              @policiesImported="getPolicies()"
            />
            <div class="modal">
              <b-modal
                ref="modalFilter"
                id="modal-filter"
                size="lg"
                class="mt-4"
                centered
              >
                <br />
                <template #modal-title> Filtrar Apólices </template>

                <form class="mb-2">
                  <div class="row">
                    <div class="col-12 col-lg-6 mb-3">
                      <label>Número</label>
                      <b-form-input
                        class="mb-3"
                        size="lg"
                        id="name-input"
                        v-model="filtersTable.number"
                        required
                      ></b-form-input>
                    </div>
                    <div class="col-12 col-lg-6 mb-3">
                      <label>Tomador</label>
                      <b-form-input
                        class="mb-3"
                        size="lg"
                        id="name-input"
                        v-model="filtersTable.policy_holder"
                        list="policyHolderList"
                        required
                      ></b-form-input>
                      <datalist id="policyHolderList">
                        <option
                          v-for="(policyHolder, index) in filteredPolicyHolders"
                          :value="policyHolder.name"
                          :key="index"
                        >
                          {{ policyHolder.name }} ({{
                            formatCnpj(policyHolder.company_cnpj)
                          }})
                        </option>
                      </datalist>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-12 col-lg-6 mb-3">
                      <label>Número da apólice</label>
                      <b-form-input
                        class="mb-3"
                        size="lg"
                        id="name-input"
                        v-model="filtersTable.external_number"
                        required
                      ></b-form-input>
                    </div>
                    <div class="col-12 col-lg-6 mb-3">
                      <label>Segurado</label>
                      <b-form-input
                        type="text"
                        v-model="filtersTable.insured"
                        list="insuredList"
                        class="mb-3"
                        size="lg"
                        id="name-input"
                        required
                      ></b-form-input>
                    </div>
                  </div>

                  <datalist id="insuredList">
                    <option
                      v-for="(insured, index) in filteredInsureds"
                      :value="insured.name"
                      :key="index"
                    >
                      <div v-if="insured && insured.document">
                        {{ insured.name }} ({{ formatCnpj(insured.document) }})
                      </div>
                      <div v-else>
                        {{ insured.name }}
                      </div>
                    </option>
                  </datalist>

                  <div class="row">
                    <div class="col-12 col-lg-6 mb-3">
                      <label>Processo</label>
                      <b-form-input
                        class="mb-3"
                        size="lg"
                        id="name-input"
                        v-model="filtersTable.process"
                        required
                      ></b-form-input>
                    </div>
                    <div class="col-12 col-lg-6 mb-3">
                      <label>Prazo para vencer</label>
                      <b-form-select
                        v-model="filtersTable.expiration_date"
                        class="mb-3"
                      >
                        <b-form-select-option value=""
                          >Sem Filtro</b-form-select-option
                        >
                        <b-form-select-option value="1"
                          >Vencida</b-form-select-option
                        >
                        <b-form-select-option value="2"
                          >Vencendo em 30 dias</b-form-select-option
                        >
                        <b-form-select-option value="3"
                          >Vencendo em 60 dias</b-form-select-option
                        >
                        <b-form-select-option value="4"
                          >Vencendo em mais de 60 dias</b-form-select-option
                        >
                      </b-form-select>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-12 col-lg-6 mb-3">
                      <label>Seguradora</label>
                      <b-form-select
                        v-model="filtersTable.insurer"
                        class="mb-3"
                      >
                        <b-form-select-option value=""
                          >Sem Filtro</b-form-select-option
                        >
                        <b-form-select-option
                          v-for="(value, index) in list_insurers"
                          :value="index"
                          :key="index"
                        >
                          {{ value }}
                        </b-form-select-option>
                      </b-form-select>
                    </div>

                    <div class="col-12 col-lg-6 mb-3">
                      <label>Modalidade</label>
                      <b-dropdown
                        variant="link"
                        toggle-class="custom-select insurance-category-toggle text-left text-decoration-none text-truncate"
                        menu-class="w-100"
                        class="d-block mb-3"
                        block
                        no-caret
                      >
                        <template #button-content>
                          {{ insuranceCategoryFilterLabel }}
                        </template>
                        <b-dropdown-form form-class="insurance-category-options">
                          <b-form-checkbox-group
                            v-model="filtersTable.insurance_category"
                            :options="list_insurance_categories"
                            value-field="id"
                            text-field="name"
                            stacked
                          ></b-form-checkbox-group>
                        </b-dropdown-form>
                        <b-dropdown-divider></b-dropdown-divider>
                        <b-dropdown-item-button
                          @click="filtersTable.insurance_category = []"
                          >Limpar seleção</b-dropdown-item-button
                        >
                      </b-dropdown>
                    </div>

                    <div class="col-12 col-lg-6 mb-3">
                      <label>Status</label>
                      <b-form-select v-model="filtersTable.status" class="mb-3">
                        <b-form-select-option value=""
                          >Sem Filtro</b-form-select-option
                        >
                        <b-form-select-option value="8"
                          >Ativa</b-form-select-option
                        >
                        <b-form-select-option value="9"
                          >Cancelada</b-form-select-option
                        >
                        <b-form-select-option value="10"
                          >Endossada</b-form-select-option
                        >
                        <b-form-select-option value="15"
                          >Em renovação</b-form-select-option
                        >
                        <b-form-select-option value="17"
                          >Renovada</b-form-select-option
                        >
                      </b-form-select>
                    </div>


                  <div class="col-12 col-lg-6 mb-3">
                    <label>Grupo Econômico</label>
                    <b-form-input
                        type="text"
                        v-model="filtersTable.economic_group"
                        list="EconomicGroupList"
                        class="mb-3"
                        size="lg"
                        id="economic-group-input"
                      ></b-form-input>
                    </div>
                    <datalist id="EconomicGroupList">
                      <option
                        v-for="(eg, index) in filteredEconomicGroups"
                        :key="index"
                        :value="eg.name"
                      >
                        {{ eg.name }}
                      </option>
                    </datalist>
                  </div>

                  <label>Vencimento</label>
                  <div id="filtros" class="row">
                    <div class="col-12 col-lg-6 mb-3">
                      <div class="input-group">
                        <div class="input-group-prepend">
                          <span class="input-group-text">de</span>
                        </div>
                        <b-form-datepicker
                          id="datepicker-dateformat1"
                          locale="pt-BR"
                          nav-button-variant="white"
                          v-model="filtersTable.start_date"
                          menu-class="w-100"
                          calendar-width="100%"
                          :show-decade-nav="false"
                          :hide-header="true"
                          placeholder="dd/mm/aaaa"
                        ></b-form-datepicker>
                      </div>
                    </div>
                    <div class="col-12 col-lg-6 mb-3">
                      <div class="input-group">
                        <div class="input-group-prepend">
                          <span class="input-group-text">até</span>
                        </div>
                        <b-form-datepicker
                          id="datepicker-dateformat2"
                          locale="pt-BR"
                          nav-button-variant="white"
                          v-model="filtersTable.end_date"
                          menu-class="w-100"
                          calendar-width="100%"
                          :show-decade-nav="false"
                          :hide-header="true"
                          placeholder="dd/mm/aaaa"
                        ></b-form-datepicker>
                      </div>
                    </div>
                  </div>
                </form>

                <template #modal-footer>
                  <b-button @click="filterPolicies" variant="outline-primary"
                    >Filtrar <i class="fa fa-search"></i
                  ></b-button>
                </template>
              </b-modal>
            </div>

            <div class="modal">
              <b-modal
                id="show-modal-create"
                ref="modalCreate"
                size="md"
                class="mt-4"
                hide-footer
                centered
              >
                <template #modal-title>Renovar apólice</template>

                <div v-if="policyRenewSelected">
                  <div class="row form">
                    <div class="col-12">
                      <p>
                        Deseja Iniciar a renovação da
                        <strong
                          >Apólice nº{{ policyRenewSelected.number }}</strong
                        >?
                      </p>
                    </div>
                    <div class="col-12">
                      <label class="dbo-ui">Tomador</label>
                      <p>
                        <strong>{{
                          policyRenewSelected.policy_holder_name
                        }}</strong>
                      </p>
                    </div>
                    <div class="col-12">
                      <label class="dbo-ui">Segurado/Beneficiário</label>
                      <p>
                        <strong>{{ policyRenewSelected.insured_name }}</strong>
                      </p>
                    </div>
                  </div>
                </div>

                <div class="d-block mt-3">
                  <div class="d-flex justify-content-between">
                    <button
                      @click="$bvModal.hide('show-modal-create')"
                      class="btn btn-outline-secondary btn-lg px-4 mr-1"
                    >
                      <i class="fa fa-angle-left mr-2"></i>
                      <span class="d-none d-md-inline-block"> Voltar</span>
                    </button>
                    <button
                      type="button"
                      class="btn btn-primary btn-lg text-uppercase px-4"
                      @click="createRenovation()"
                    >
                      <b-spinner v-if="loading" small></b-spinner> Renovar
                    </button>
                  </div>
                </div>
              </b-modal>
            </div>
                <div class="modal">
      <!-- Modal Importar PDFs via IA -->
      <UploadPdfPolicies
        v-model="showUploadPdfModal"
        @imported="getPolicies"
      />
    </div>
            <div class="modal">
              <b-modal
                id="show-modal"
                ref="showModal"
                size="md"
                class="mt-4"
                hide-footer
                centered
              >
                <br />
                <template #modal-title>
                  {{ title }}
                </template>

                <div class="card card-termo-uso mt-1 mb-5">
                  <p class="my-4">{{ deletion_data }}</p>
                </div>
                <div class="row">
                  <div class="col-12 col-lg-12">
                    <button
                      @click="$bvModal.hide('show-modal')"
                      class="btn btn-outline-secondary btn-lg text-uppercase px-4 mr-1"
                    >
                      <i class="fa fa-angle-left mr-2"></i
                      ><span class="d-none d-md-inline-block"> Voltar</span>
                    </button>
                    <button
                      @click="destroyPolicy()"
                      class="btn btn-primary btn-lg text-uppercase px-4"
                      data-toggle="modal"
                      data-target="#modal-cnpj"
                    >
                      Excluir<i class="fa fa-angle-right ml-2"></i>
                    </button>
                  </div>
                </div>
              </b-modal>

              <b-modal
                id="show-modal-automatic-renewal"
                ref="modalAutomaticRenewal"
                size="lg"
                hide-footer
              >
                <template #modal-title>Renovar apólice</template>
                <AutomaticPolicyRenewal :policy-id="policyIdSelected" />
              </b-modal>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import policyService from "@/services/policy";
import policyRenovationService from "@/services/policy-renovation";
import numericFormatterMixin from "@/mixins/numericFormatter";
import dateFormatter from "@/mixins/dateFormatter";
import commomsHelper from "@/mixins/commomsHelper";
import uiHelperMixin from "@/mixins/uiHelper";
import tableHelperMixin from "@/mixins/tableHelper";
import translateInsuranceTypes from "@/enums/translate/insuranceTypes";
import insuranceCategoryTecnicTypes from "@/enums/translate/insuranceCategoryTecnicTypes";
import fileDownload from "js-file-download";
import ImportPoliciesModal from "@/components/Policy/ImportPolicies/Index.vue";
import UploadPdfPolicies from "@/components/Policy/UploadPdfPolicies/Index.vue";
import translateStatusQuotationTypes from "@/enums/translate/statusQuotationTypes";
import translateInsurerIdentifierTypes from "@/enums/translate/insurerIdentifierTypes";
import authenticationService from "@/services/authentication";
import companyService from "@/services/company";
import AutomaticPolicyRenewal from '@/components/Policy/Renewal/AutomaticPolicyRenewal.vue';

export default {
  name: "list-policies",
  components: {
    ImportPoliciesModal,
    UploadPdfPolicies,
    AutomaticPolicyRenewal,
  },
  mixins: [
    numericFormatterMixin,
    dateFormatter,
    commomsHelper,
    uiHelperMixin,
    tableHelperMixin,
  ],
  data: () => ({
    translateInsuranceTypes: translateInsuranceTypes,
    insuranceCategoryTecnicTypes: insuranceCategoryTecnicTypes,
    items: [],
    title: "Excluir apólices",
    deletion_data: "",
    current_deletion: null,
    showImportPolicies: false,
    list_insurers: [],
    list_insurance_categories: [],
    dateNow: new Date(),
    rows: 0,
    loading: false,
    loadingExport: false,
    nameTable: "policies",
    filtersTable: {
      policy_holder: "",
      process: "",
      insured: "",
      insurer: "",
      insurance_category: [],
      expiration_date: "",
      start_date: "",
      end_date: "",
      number: "",
      external_number: "",
      economic_group: "",
      status: "",
    },
    translateStatusQuotationTypes: translateStatusQuotationTypes,
    translateInsurerIdentifierTypes: translateInsurerIdentifierTypes,
    policyRenewSelected: {},
    status: [],
    insureds: [],
    policyHolders: [],
    showUploadPdfModal: false,
    economicGroups: [],
    insuranceCategoriesWithIntegrations: [],
    policyIdSelected: null,
  }),
  created() {
    this.dateNow.setHours(23, 59, 59, 999);
    
    this.getInsuranceCategoriesWithIntegrations();

    if (this.$route.query.filter) {
      this.fillFilterForm();
      const category = this.filtersTable.insurance_category;
      this.filtersTable.insurance_category = (Array.isArray(category)
        ? category
        : [category].filter(Boolean)
      ).map(Number);
      this.getPolicies();
      this.fetchInsuranceData();
      return;
    }

    this.cleanFilter();
    this.fetchInsuranceData();
  },
  methods: {
    getPolicies() {
      this.resetAlert();
      this.loading = true;
      policyService
        .get(this.getFilterForm())
        .then((response) => {
          if (response.data.error) {
            this.showError(response.data.message);
            return;
          }
          this.rows = response.data.total;
          this.formatList(response.data);
          this.loading = false;
        })
        .catch((e) => {
          console.log(e);

          this.showError("Não foi possível recuperar as apólices.");
        });
    },
    getInsurers() {
      this.resetAlert();
      policyService
        .getInsurers()
        .then((response) => {
          this.list_insurers = response.data;
        })
        .catch(() => {
          this.showError("Não foi possível recuperar a lista de seguradoras.");
        });
    },

    getInsuranceCategories() {
      this.resetAlert();
      policyService
        .getInsuranceCategories()
        .then((response) => {
          this.list_insurance_categories = response.data.map((element) => ({
            id: element.id,
            name: element.tecnic_name,
          }));
        })
        .catch(() => {
          this.showError("Não foi possível recuperar a lista de modalidades.");
        });
    },
    getInsuranceCategoriesWithIntegrations() {
      policyService
        .getAllInsuranceCategoriesWithIntegrations()
        .then((response) => {
          this.insuranceCategoriesWithIntegrations = response.data;
        })
        .catch(() => {
          this.showError("Não foi possível recuperar a lista de integrações.");
        });
    },
    hasInsuranceCategoryIntegration(insuranceCategoryId) {
      return this.insuranceCategoriesWithIntegrations.some(
        item => item.id === insuranceCategoryId
      );
    },

    addHtmlClass(data) {
      const today = new Date();
      const date1 = new Date(this.formatDateISO(today));
      const date2 = new Date(data.deadline.split(" ")[0]);
      const differenceInTime = date2.getTime() - date1.getTime();
      const differenceInDays = differenceInTime / (1000 * 3600 * 24);
      const formattedDate = this.formatDateTimeCustom(date2).slice(0, 10);

      switch (true) {
        case differenceInDays < 0:
          return `<div class="badge badge-dark rounded-pill badge-custom-pill text-uppercase px-4 py-2 font-12"> ${formattedDate}</div> `;

        case differenceInDays > 0 && differenceInDays < 30:
          return (
            '<div class="badge badge-danger rounded-pill badge-custom-pill text-uppercase px-4 py-2 font-12">' +
            differenceInDays +
            " dias</div>"
          );

        case differenceInDays >= 30 && differenceInDays < 60:
          return (
            '<div class="badge badge-warning rounded-pill badge-custom-pill text-white text-uppercase px-4 py-2 font-12">' +
            differenceInDays +
            " dias</div>"
          );

        case differenceInDays >= 60:
          return (
            '<div class="badge badge-success rounded-pill badge-custom-pill text-uppercase px-4 py-2 font-12">' +
            differenceInDays +
            " dias</div>"
          );

        case differenceInDays == 0:
          return '<div class="badge badge-danger rounded-pill badge-custom-pill text-uppercase px-4 py-2 font-12">Vence hoje</div>';
        default:
          return "";
      }
    },
    formatList(data) {
      this.items = data.data.map((element) => ({
        id: element.id,
        number: element.number,
        insurance_type_id: this.getEnumKeyByEnumValue(
          this.insuranceCategoryTecnicTypes,
          element.insurance_category_id
        ),
        insurance_category_id: element.insurance_category_id,
        contract_value: element.contract_value,
        policy_holder_name: element.policy_holder_name,
        insured_name: element.insured_name,
        insurance_premium: element.insurance_premium,
        validity_expiration: element.validity_expiration,
        deadline: element.validity_expiration,
        insurer: element.insurer_selected,
        other_insurer: element.other_insurer,
        action: element.id,
        status: element.status,
        renovations: element.renovations,
        icon: element.icon,
      }));
    },
    redirect(routeName, params = null, query = null) {
      this.$router.push({ name: routeName, params: params, query: query });
    },
    downloadReport() {
      this.loadingExport = true;
      policyService
        .downloadReport(this.getFilterForm())
        .then((response) => {
          this.loadingExport = false;
          if (response.data.error) {
            this.showError(response.data.message);
            return;
          }
          fileDownload(response.data, "policies-export.xlsx");
          policyService.deleteFilePolicy();
        })
        .catch(() => {
          this.showError("Não foi possível realizar o download deste arquivo.");
        });
    },
    openImportPoliciesModal() {
      this.showImportPolicies = true;
    },
    isMaster() {
      return authenticationService.isMaster();
    },
    showModalDestroy(data) {
      this.current_deletion = data.action;
      this.deletion_data =
        "Deseja realmente excluir os dados de " +
        data.policy_holder_name +
        " ?";
      this.$refs.showModal.show();
    },
    destroyPolicy() {
      this.loading = true;
      this.$refs.showModal.hide();
      policyService
        .destroy(this.current_deletion)
        .then((response) => {
          if (response.data.error) {
            this.showError(response.data.message);
            return;
          }
          this.getPolicies();
        })
        .catch(() => {
          this.showError("Não foi possível remover a cotação.");
        })
        .finally(() => {
          this.current_deletion = null;
          this.checkPolice = true;
          this.loading = false;
        });
    },
    filterPolicies() {
      this.setFilterForm();
      this.getPolicies();
      this.$refs.modalFilter.hide();
    },
    cleanFilter() {
      this.filtersTable = {
        policy_holder: "",
        process: "",
        insured: "",
        insurer: "",
        insurance_category: [],
        expiration_date: "",
        start_date: "",
        end_date: "",
        number: "",
        external_number: "",
        economic_group: "",
        status: "",
      };
      this.setFilterForm();
      this.getPolicies();
    },
    sortingTableChanged(e) {
      this.sortBy = e.sortBy;
      this.sortDesc = e.sortDesc;
      this.setFilterForm();
      this.getPolicies();
    },
    showModalCreate(policy) {
      if (policy) {
        this.policyRenewSelected = policy;
        this.$refs.modalCreate.show();
      }
    },
    createRenovation() {
      if (!this.policyRenewSelected) {
        this.showError("Não foi possível iniciar a renovação.");
        return;
      }

      this.loading = true;
      policyRenovationService
        .store(this.policyRenewSelected.id)
        .then((response) => {
          if (response.data.error) {
            this.showError(response.data.message);
            return;
          }
          this.redirect(
            "garantee-data",
            {
              quotationId: response.data.quotation_id,
            },
            {
              isRenewal: true,
            }
          );
        })
        .catch(() => {
          this.showError("Não foi possível remover a cotação.");
        })
        .finally(() => {
          this.policyRenewSelected = {};
          this.loading = false;
          this.$refs.modalCreate.hide();
        });
    },
    getIndexFilterValues() {
      this.loading = true;
      policyService
        .getIndexFilterValues()
        .then((response) => {
          if (response.data.error) {
            this.showError(response.data.message);
            return;
          }
          this.policyHolders = response.data.policy_holders;
          this.insureds = response.data.insureds;
        })
        .catch(() => {
          this.showError("Não foi possível remover a cotação.");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchInsuranceData() {
      this.getInsurers();
      this.getInsuranceCategories();
      this.getIndexFilterValues();
      this.getEconomicGroupsFilter();
    },
    truncateText(text, maxChar = 28) {
      return text.length > maxChar ? `${text.substring(0, maxChar)}...` : text;
    },
    imageLoadError(idx) {
      this.$set(this.items[idx], "no_logo", true);
    },

    openUploadPoliciesModal() {
      this.showUploadPdfModal = true;
    },
    pageChanged(bvEvent, pageNumber) {
      this.currentPage = pageNumber;
      this.setFilterForm();
      this.getPolicies();
    },
    getEconomicGroupsFilter() {
      this.resetAlert();
      companyService
        .getEconomicGroupsFilter()
        .then((response) => {
          if (response.data?.error) {
            this.showError(response.data.message);
            return;
          }
          const norm = (s) =>
            (s || '')
              .toString()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .toLowerCase();

          const rows = Array.isArray(response.data) ? response.data : [];
          this.economicGroups = rows.map((r) => {
            const name = r.group_name || '';
            return {
              name,
              _name: norm(name),
            };
          });
        })
        .catch(() => {
          this.showError('Não foi possível resgatar grupos econômicos para os filtros!');
        });
    },
    
    showModalAutomaticRenewal(policyId) {
      this.policyIdSelected = policyId;
      this.$refs.modalAutomaticRenewal.show();
    },
    isInRenewal(item) {
      return (
        !!item.renovations[0] &&
        [
          this.statusQuotationTypes.AWAITING_DOCUMENTATION,
          this.statusQuotationTypes.AWAITING_APPROVAL
        ].includes(item.renovations[0].status_id)
      );
    },
    getStatusId(item) {
      return this.isInRenewal(item)
        ? item.renovations[0].status_id
        : item.status;
    },
    isRenewedAndFinished(item) {
      return (
        item.status === this.statusQuotationTypes.RENEWED &&
        !!item.renovations[0] &&
        item.renovations[0].status_id === this.statusQuotationTypes.FINISHED
      );
    },
  },
  computed: {

    filteredInsureds() {
      if (this.filtersTable.insured && this.filtersTable.insured.length >= 3) {
        return this.insureds
          .filter(
            (insured) =>
              insured.insured_name &&
              insured.insured_name
                .toLowerCase()
                .includes(this.filtersTable.insured.toLowerCase())
          )
          .map((insured) => ({
            name: insured.insured_name,
            document: insured.insured_document,
          }));
      }
      return [];
    },
    totalPages() {
      return Math.ceil(this.rows / this.perPage);
    },
    pages() {
      const total = this.totalPages;
      if (total <= 3) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      if (this.currentPage === 1) {
        return [1, 2, 3];
      }
      if (this.currentPage === total) {
        return [total - 2, total - 1, total];
      }
      return [this.currentPage - 1, this.currentPage, this.currentPage + 1];
    },
    filteredPolicyHolders() {
      if (
        this.filtersTable.policy_holder &&
        this.filtersTable.policy_holder.length >= 3
      ) {
        return this.policyHolders
          .filter(
            (policy_holder) =>
              policy_holder.policy_holder_name &&
              policy_holder.policy_holder_name
                .toLowerCase()
                .includes(this.filtersTable.policy_holder.toLowerCase())
          )
          .map((policy_holder) => ({
            name: policy_holder.policy_holder_name,
            company_cnpj: policy_holder.policy_holder_document,
          }));
      }
      return [];
    },
    filteredEconomicGroups() {
      const raw = (this.filtersTable.economic_group || '').trim();
      const norm = (s) =>
        (s || '')
          .toString()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();

      const q = norm(raw);
      if (q.length < 3) return [];

      const rank = (g) => {
        if (g._name.startsWith(q)) return 0;
        if (g._name.includes(' ' + q)) return 1;
        if (g._name.includes(q)) return 2;
        return 9;
      };

      return this.economicGroups
        .filter((g) => g._name.includes(q))
        .sort((a, b) => rank(a) - rank(b) || a._name.localeCompare(b._name));
    },
    insuranceCategoryFilterLabel() {
      const selected = this.filtersTable.insurance_category || [];
      if (selected.length === 0) return "Sem Filtro";
      if (selected.length === 1) {
        const category = this.list_insurance_categories.find(
          (item) => item.id === selected[0]
        );
        return category ? category.name : "1 selecionada";
      }
      return `${selected.length} selecionadas`;
    },
  },
};
</script>

<style scoped>
::v-deep .insurance-category-toggle.btn {
  color: #495057 !important;
}

::v-deep .insurance-category-options {
  max-height: 260px;
  overflow-y: auto;
}
</style>
